import { ReactNode, forwardRef, ReactElement, Ref } from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
  Dialog,
  Slide,
  Stack,
  Box,
  Card,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Grid,
} from '@mui/material';
import {
  mobileCardSx,
  cardHeaderSx,
  cardSx,
} from 'utils';
import { Cancel } from '@mui/icons-material';
import { DigitalSignature } from 'types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction='up' ref={ref} {...props} />;
});

interface DigitalSignatureDialogProps {
  open: boolean;
  signature: DigitalSignature;
  close: () => void;
}

const DigitalSignatureDialog = ({
  open,
  close,
  signature,
}: DigitalSignatureDialogProps): ReactNode => {
  const { palette, breakpoints } = useTheme();
  const { t } = useTranslation();
  const mobile = useMediaQuery(breakpoints.down('lg'));

  const signatureDetails = `
  ${t('job.signatures.signedByUserName', {
    ns: 'entities',
  })}: ${signature.signedByUserName}
  ${t('at')} ${dayjs(signature.signedAt).format('MM/DD/YYYY hh:mm A')}
  `;

  return (
    <Dialog
      open={open}
      onClose={close}
      TransitionComponent={Transition}
      scroll={'body'}
      fullWidth
      aria-label={signature.description}
      maxWidth={'md'}
      fullScreen={mobile}
      sx={{ mt: { xs: -1, lg: 0 }, pb: 0 }}
    >
      <Card raised sx={mobile ? mobileCardSx : cardSx}>
        <Box
          sx={
            mobile
              ? {
                  display: 'flex',
                  p: { xs: 2, sm: 4 },
                  backgroundColor: palette.secondary.main,
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  alignItems: 'center',
                  width: '100%',
                }
              : cardHeaderSx
          }
        >
          <Box sx={{ flex: 1, alignItems: 'center', display: 'flex' }}>
            <Typography
              variant={'h4'}
              sx={{
                color: palette.secondary.contrastText,
                textAlign: 'left',
                textTransform: 'capitalize',
              }}
            >
              {signature.description}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              variant={'text'}
              onClick={close}
              sx={{
                px: 1,
                textTransform: 'uppercase',
                color: palette.secondary.contrastText,
              }}
              endIcon={
                <Cancel
                  htmlColor={palette.secondary.contrastText}
                  fontSize={'large'}
                />
              }
            >
              {t('close')}
            </Button>
          </Box>
        </Box>
        <Stack
          direction={'column'}
          padding={2}
          sx={{ alignItems: 'center', justifyContent: 'center' }}
        >
          <img
            src={signature.imageURL}
            alt={signature.description}
            style={{
              maxWidth: '100%',
              height: 'auto',
              maxHeight: 120,
              display: 'block',
              objectFit: 'contain',
            }}
          />
          <Stack direction={'row'} spacing={1.5} sx={{ width: '100%', alignItems: 'center', justifyContent: 'center'}}>
              <Typography variant={'h6'}>{signatureDetails}</Typography>


          </Stack>
        </Stack>
      </Card>
    </Dialog>
  );
};

export default DigitalSignatureDialog;
